<template>
  <div>
    <Breadcrumbs :items="breadcrumbs" />
    <div class="trial-responses-table">
      <div class="table-container">
        <div class="inner-scroll">
          <table>
            <thead>
              <td v-for="(heading, keyIndex) in headers" :key="keyIndex" >
                {{heading}}
              </td>
            </thead>
            <tbody>
              <tr v-for="(row, rowIndex) in trialResponses" :key="rowIndex" >
                <td :class="getClass(row)">
                  {{row['subject_id']}}
                </td>
                <td v-for="(q, i) in questionnaires" :key="i">
                  <div class="button-cell">
                    <input type="checkbox"
                      onclick="return false;"
                      :title="row[q]['status']"
                      :checked="getIsChecked(row[q]['status'])" />
                    <rp-button 
                      @click.stop="onResend(row, row[q])"
                      :disabled="getResendDisabled(row, row[q]['status'])">
                      Resend
                    </rp-button>
                  </div>
                </td>
                <td>
                  <rp-button class="removebtn"
                    @click.stop="onRemove(row)"
                    :disabled="getRemoveDisabled(row)">
                    Remove Patient
                  </rp-button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <rp-overlay :active="showConfirmRemove">
      <rp-confirm
        icon="warning_rounded"
        :messageText="confirmRemoveMessage"
        :confirm="doRemove"
        :cancel="closeConfirm" />
    </rp-overlay>

    <rp-overlay :active="showOverlay">
      <rp-alert
        icon="done"
        message="A reminder to complete this questionnaire has been sent to the patient"
        :onClick="closeOverlay" />
    </rp-overlay> 

  </div>
</template>

<script>
import page from '@/mixins/page'
import Breadcrumbs from '@/components/Breadcrumbs'
import Button from 'raypro_ui/src/components/ui/inputs/Button'
import Overlay from 'raypro_ui/src/components/ui/Overlay'
import Confirm from 'raypro_ui/src/components/ui/DeleteConfirm'
import Alert from 'raypro_ui/src/components/ui/Alert'
import {getTrialResponses, sendReminder, deleteTrialPatient} from 'raypro_ui/src/api/trials'

export default {
  mixins: [page],

  components: {
    'rp-button': Button,
    'rp-overlay': Overlay,
    'rp-confirm': Confirm,
    'rp-alert': Alert,
    Breadcrumbs,
  },

  data() {
    return {
      trialResponses: [],
      headers: ['Subject ID', '7 Days', '1 Month', '3 Months', '1 Year', '2 Year', '3 Year'],
      questionnaires: ['one_week', 'one_month', 'three_month', 'one_year', 'two_year', 'three_year'],
      breadcrumbs: [{label: 'Responses'}],
      showConfirmRemove: false,
      confirmRemoveMessage: '',
      delPatientId: '',
      showOverlay: false,
    }
  },

  computed: {
    trialId() {
      return this.$route.params.id
    },

    surgeonId() {
      return this.$route.params.surgeonId
    },
  },

  async mounted() {
    await this.getPatients()
  },

  methods: {
    async getPatients() {
      const {data} = await getTrialResponses(this.trialId, this.surgeonId)
      this.trialResponses = data
    },

    getIsChecked(status) {
      return status === 'completed'
    },

    getResendDisabled(row, status) {
      return row.deleted || status !== 'waiting'
    },

    getRemoveDisabled(row) {
      return row.deleted === true
    },

    getClass(row) {
      return (row.deleted) ? 'strikethrough' : ''
    },

    async onResend(row, questionnaire) {
      let payload = {
        patient_id: row.id,
        questionnaire_id: questionnaire.id,
      }

      await sendReminder(this.trialId, {reminder: payload})
      this.showOverlay = true
    },

    onRemove(row) {
      this.delPatientId = row.id
      this.confirmRemoveMessage = `Are you sure you want to remove patient ${row.subject_id} from the study?`
      this.showConfirmRemove = true
    },

    async doRemove() {
      this.showConfirmRemove = false
      await deleteTrialPatient(this.trialId, this.delPatientId)
      await this.getPatients()
    },

    closeConfirm() {
      this.showConfirmRemove = false
    },

    closeOverlay() {
      this.showOverlay = false
    },
  },
}
</script>

<style lang="sass" scoped>
  @import 'raypro_ui/src/styles/base/_themes.scss'
  @import 'raypro_ui/src/styles/base/_variables.sass'
  @import 'raypro_ui/src/styles/helpers/_mixins.sass'

  .trial-responses-table
    color: $color-text
    background-color: $color-panel-bg
    border-radius: 10px

    button
      margin-top: 0
      &.removebtn
        width: 80px

    table
      padding: 10px
      width: 100%
      border-collapse: collapse
      table-layout: fixed

      td
        &.tdbutton
          width: 20px

        &.strikethrough
          text-decoration: line-through

        div
          &.button-cell
            display: flex
            align-items: center
            gap: 10px

            button
              &.btn
                height: 22px
                padding: 0px 5px    

        .status
          display: block

        div
          span
            display: flex
            justify-content: center

    thead
      @include themify($themes)
        background: themed('thead-background')
        color: themed('thead-color')

      font-weight: bold
      text-transform: uppercase
    
      td
        white-space: nowrap
        line-height: 1

        &.tdbutton
          text-align: center

        @include themify($themes)
          padding: themed('thead-td-padding')
          font-weight: themed('thead-td-weight')
          font-size: themed('thead-font-size')
          
        &:first-child 
          @include themify($themes)
            border-radius: themed('td-radius-left')

        &:last-child
          @include themify($themes)
            border-radius: themed('td-radius-right')

    tr
      @include themify($themes)
        border-bottom: themed('tr-border-bottom')
    
      &:first-child
        @include themify($themes)
          border-top: themed('tr-border-top')
      &:last-child
        @include themify($themes)
          border-bottom: 0
      td
        @include themify($themes)
          padding: themed('td-padding')
        
        font-size: 12px

    .pagination
      @include themify($themes)
        border-top: themed('tr-border-top')
</style>



// WEBPACK FOOTER //
// src/components/pages/Clinical/TrialResponses.vue